import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  FormControlLabel,
  Paper
} from '@mui/material';
import {
  Description as DocumentIcon,
  Psychology as AIIcon,
  CheckCircleOutline as SuccessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

import { apiClient } from '@/services/api';

interface ProjectInfo {
  id: string;
  project_name: string;
  industry_type: string;
  client_level: string;
  evaluation_type: string;
  description: string;
}

interface DocumentInfo {
  filename: string;
  size: number;
  modified_time: string;
}

interface PerformanceIndicatorAIDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectInfo | null;
  onRecommendationComplete: (result: any) => void;
}

export default function PerformanceIndicatorAIDialog({
  open,
  onClose,
  project,
  onRecommendationComplete
}: PerformanceIndicatorAIDialogProps) {
  // 状态管理
  const [step, setStep] = useState(0); // 0: 选择文档, 1: 推荐中, 2: 显示结果
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<any>(null);
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);

  // 当对话框打开时，重置状态
  useEffect(() => {
    if (!open) {
      // 重置状态
      setStep(0);
      setDocuments([]);
      setSelectedDocuments([]);
      setLoading(false);
      setError(null);
      setRecommendation(null);
      setSelectedIndicators([]);
    } else {
      // 自动获取文档列表
      fetchDocuments();
    }
  }, [open, project]);

  // 监听全局文档刷新事件
  useEffect(() => {
    const handleGlobalRefresh = () => {
      // 只有在对话框打开时才刷新文档列表
      if (open) {
        fetchDocuments();
      }
    };

    window.addEventListener('documentsShouldRefresh', handleGlobalRefresh);

    return () => {
      window.removeEventListener('documentsShouldRefresh', handleGlobalRefresh);
    };
  }, [open, project]);

  // 获取项目文档列表
  const fetchDocuments = async () => {
    if (!project) {
      setError('项目信息未加载，请稍后重试');
      return;
    }
    
    setError(null);
    
    try {
      const responseData = await apiClient.get(`/projects/${project.id}/performance-indicators/ai/documents`);
      
      if (!responseData) {
        throw new Error('API响应为空');
      }
      
      if (responseData.success !== true) {
        throw new Error(`API响应success为false或不存在: ${responseData.success}`);
      }
      
      if (!responseData.data || !responseData.data.documents) {
        throw new Error('API响应中缺少data.documents字段');
      }
      
      const documents = responseData.data.documents;
      
      // 过滤出markdown和文本文档
      const textDocs = documents.filter((doc: DocumentInfo) => 
        doc.filename.toLowerCase().endsWith('.md') || 
        doc.filename.toLowerCase().endsWith('.txt') ||
        doc.filename.includes('MD_') ||
        doc.filename.includes('TXT_') ||
        doc.filename.includes('DOCX_')
      );
      
      setDocuments(textDocs);
      
      if (textDocs.length === 0) {
        throw new Error('请先上传项目资料文档，或等待文档解析完毕（状态变为"已完成"）后再使用AI推荐功能');
      }
      
      
    } catch (err: any) {
      setError(`文档数据获取错误: ${err.message || '网络错误'}`);
    }
  };

  // 处理文档选择
  const handleDocumentToggle = (document: DocumentInfo) => {
    setSelectedDocuments(prev => {
      const isSelected = prev.some(doc => doc.filename === document.filename);
      if (isSelected) {
        return prev.filter(doc => doc.filename !== document.filename);
      } else {
        return [...prev, document];
      }
    });
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments([...documents]);
    }
  };

  // 开始推荐指标
  const handleStartRecommendation = async () => {
    if (!project) {
      setError('项目信息未加载，请稍后重试');
      return;
    }
    
    if (selectedDocuments.length === 0) {
      setError('请至少选择一个文档');
      return;
    }
    
    setStep(1);
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/projects/${project.id}/performance-indicators/ai/recommend`, {
        project_info: {
          project_name: project.project_name,
          industry_type: project.industry_type,
          client_level: project.client_level,
          evaluation_type: project.evaluation_type,
          description: project.description
        },
        selected_documents: selectedDocuments.map(doc => doc.filename),
        user_requirement: ''
      }, { timeout: 180000 }); // 3分钟超时
      
      
      if (response && response.success) {
        setRecommendation(response);
        // 自动选中所有推荐的指标
        const allIndicatorNames = response.data.recommendedIndicators.map((indicator: any) => indicator.indicator_name);
        setSelectedIndicators(allIndicatorNames);
        setStep(2);
      } else {
        throw new Error(response?.error || '推荐结果格式错误');
      }
    } catch (err: any) {
      setError(err.message);
      setStep(0); // 回到选择文档步骤
    } finally {
      setLoading(false);
    }
  };

  // 处理指标选择
  const handleIndicatorSelection = (indicatorName: string, checked: boolean) => {
    if (checked) {
      setSelectedIndicators(prev => [...prev, indicatorName]);
    } else {
      setSelectedIndicators(prev => prev.filter(name => name !== indicatorName));
    }
  };

  // 处理全选
  const handleSelectAllIndicators = (checked: boolean) => {
    if (checked && recommendation) {
      const allIndicatorNames = recommendation.data.recommendedIndicators.map((indicator: any) => indicator.indicator_name);
      setSelectedIndicators(allIndicatorNames);
    } else {
      setSelectedIndicators([]);
    }
  };

  // 应用推荐结果
  const handleApplyRecommendation = () => {
    
    if (recommendation && selectedIndicators.length > 0) {
      // 只传递选中的指标
      const selectedIndicatorsList = recommendation.data.recommendedIndicators.filter((indicator: any) => 
        selectedIndicators.includes(indicator.indicator_name)
      );
      
      
      const modifiedRecommendation = {
        ...recommendation,
        data: {
          ...recommendation.data,
          recommendedIndicators: selectedIndicatorsList,
          totalIndicatorCount: selectedIndicatorsList.length
        }
      };
      
      onRecommendationComplete(modifiedRecommendation);
      
      // 延迟关闭对话框，确保处理完成
      setTimeout(() => {
        onClose();
      }, 500);
    } else {
      onClose();
    }
  };

  // 渲染步骤内容
  const renderStepContent = () => {
    switch (step) {
      case 0: // 选择文档
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              选择用于推荐绩效指标的项目资料
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              请选择包含项目信息、业务流程、预期成果等内容的文档，AI将基于这些材料推荐合适的绩效指标体系。
            </Typography>
            
            {documents.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={handleSelectAll}
                >
                  {selectedDocuments.length === documents.length ? '取消全选' : '全选'}
                </Button>
                <Typography variant="caption" sx={{ ml: 2 }}>
                  已选择 {selectedDocuments.length} / {documents.length} 个文档
                </Typography>
              </Box>
            )}
            
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {documents.map((document) => (
                <ListItem 
                  key={document.filename}
                  button
                  onClick={() => handleDocumentToggle(document)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedDocuments.some(doc => doc.filename === document.filename)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemIcon>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={document.filename}
                    secondary={
                      <Box>
                        <Typography variant="caption" component="div">
                          大小: {Math.round(document.size / 1024)}KB
                        </Typography>
                        <Typography variant="caption" component="div">
                          修改时间: {new Date(parseFloat(document.modified_time) * 1000).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );
        
      case 1: // 推荐中
        return (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <AIIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              AI正在分析文档内容
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              基于 {selectedDocuments.length} 个文档推荐绩效指标体系...
            </Typography>
            <LinearProgress sx={{ width: '100%', mt: 2 }} />
          </Box>
        );
        
      case 2: // 显示结果
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SuccessIcon sx={{ color: 'success.main', mr: 1 }} />
              <Typography variant="h6">
                AI推荐绩效指标 ({recommendation?.data?.recommendedIndicators?.length || 0}个) - 请选择需要创建的指标
              </Typography>
            </Box>
            
            {recommendation && recommendation.data && (
              <Box>
                {/* 全选控制 */}
                <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedIndicators.length === recommendation.data.recommendedIndicators.length}
                        indeterminate={selectedIndicators.length > 0 && selectedIndicators.length < recommendation.data.recommendedIndicators.length}
                        onChange={(e) => handleSelectAllIndicators(e.target.checked)}
                      />
                    }
                    label={`全选 (已选择 ${selectedIndicators.length} / ${recommendation.data.recommendedIndicators.length})`}
                  />
                </Box>

                {/* 指标列表 (分组) */}
                <Stack spacing={2}>
                  {Object.entries(
                    recommendation.data.recommendedIndicators.reduce((acc: any, indicator: any) => {
                      const { level1_name, level2_name } = indicator;
                      if (!acc[level1_name]) acc[level1_name] = {};
                      if (!acc[level1_name][level2_name]) acc[level1_name][level2_name] = [];
                      acc[level1_name][level2_name].push(indicator);
                      return acc;
                    }, {})
                  ).map(([level1_name, level2_groups]: [string, any]) => (
                    <Accordion key={level1_name} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          {level1_name}
                          <Chip 
                            label={`共 ${Object.values(level2_groups).flat().length} 个`} 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 2 }} 
                          />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1.5}>
                          {Object.entries(level2_groups).map(([level2_name, indicators]: [string, any]) => (
                            <Paper key={level2_name} variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                {level2_name}
                                <Chip 
                                  label={`共 ${indicators.length} 个`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ ml: 1.5 }}
                                />
                              </Typography>
                              <Stack spacing={1}>
                                {indicators.map((indicator: any, index: number) => (
                                  <Paper key={index} elevation={0} sx={{ p: 1.5, bgcolor: 'action.selected' }}>
                                    <Box display="flex" alignItems="flex-start">
                                      <Checkbox
                                        checked={selectedIndicators.includes(indicator.indicator_name)}
                                        onChange={(e) => handleIndicatorSelection(indicator.indicator_name, e.target.checked)}
                                        sx={{ mt: -0.5 }}
                                      />
                                      <Box flex={1} ml={1}>
                                        <Typography variant="subtitle2">
                                          {indicator.indicator_name}
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                          <Chip 
                                            label={`指标值: ${indicator.indicator_value || 'N/A'}`} 
                                            size="small" 
                                            color="primary" 
                                          />
                                          <Chip 
                                            label={indicator.recommendation_source || 'AI推荐'} 
                                            size="small" 
                                            color="info" 
                                          />
                                        </Stack>
                                        {indicator.value_description && (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                              指标值说明:
                                            </Typography>
                                            <pre style={{ 
                                              whiteSpace: 'pre-wrap', 
                                              wordBreak: 'break-all', 
                                              margin: 0, 
                                              fontSize: '0.8rem',
                                              color: '#666',
                                              maxHeight: '120px',
                                              overflow: 'auto',
                                              backgroundColor: '#f5f5f5',
                                              padding: '8px',
                                              borderRadius: '4px',
                                              border: '1px solid #e0e0e0'
                                            }}>
                                              {indicator.value_description}
                                            </pre>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </Paper>
                                ))}
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        );
        
      default:
        return null;
    }
  };

  // 渲染操作按钮
  const renderActions = () => {
    switch (step) {
      case 0: // 选择文档
        return (
          <>
            <Button onClick={onClose}>
              取消
            </Button>
            <Button 
              variant="contained" 
              onClick={handleStartRecommendation}
              disabled={selectedDocuments.length === 0}
            >
              开始推荐指标
            </Button>
          </>
        );
        
      case 1: // 推荐中
        return (
          <Button onClick={onClose} disabled={loading}>
            取消
          </Button>
        );
        
      case 2: // 显示结果
        return (
          <>
            <Button onClick={onClose}>
              关闭
            </Button>
            <Button 
              variant="contained" 
              onClick={handleApplyRecommendation}
              disabled={selectedIndicators.length === 0}
            >
              应用选中指标 ({selectedIndicators.length})
            </Button>
          </>
        );
        
      default:
        return (
          <Button onClick={onClose}>
            关闭
          </Button>
        );
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        if (loading || step === 1) return; // 推荐处理中也不允许关闭
        onClose();
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      disableEscapeKeyDown
      keepMounted
    >
      <DialogTitle>
        AI推荐绩效指标
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
}
